import { Controller } from "@hotwired/stimulus"
import SignaturePad from "signature_pad"

export default class extends Controller {
  static targets = [
    "clientCanvas", "clientSignatureInput", "clientSection",
    "employeeCanvas", "employeeSignatureInput", "employeeSection",
    "submitButton"
  ]
  static values = {
    clientSigned: Boolean
  }

  connect() {
    // Initialize client signature pad only
    this.initClientSignaturePad()

    // Employee signature pad will be initialized when section becomes visible
    this.employeeSignaturePad = null

    // Listen for window resize
    this.resizeHandler = this.handleResize.bind(this)
    window.addEventListener("resize", this.resizeHandler)

    // Initial state: hide employee section
    this.updateVisibility()
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
  }

  initClientSignaturePad() {
    const canvas = this.clientCanvasTarget
    this.setupCanvasSize(canvas)
    
    this.clientSignaturePad = new SignaturePad(canvas, {
      backgroundColor: "rgba(255, 255, 255, 1)",
      penColor: "rgb(0, 0, 0)",
      minWidth: 0.5,
      maxWidth: 2.5,
      throttle: 16,
      minDistance: 5
    })

    this.clientSignaturePad.addEventListener("endStroke", () => {
      this.updateClientSignatureInput()
    })
  }

  initEmployeeSignaturePad() {
    if (this.employeeSignaturePad) return // Already initialized

    const canvas = this.employeeCanvasTarget
    this.setupCanvasSize(canvas)
    
    this.employeeSignaturePad = new SignaturePad(canvas, {
      backgroundColor: "rgba(255, 255, 255, 1)",
      penColor: "rgb(0, 0, 0)",
      minWidth: 0.5,
      maxWidth: 2.5,
      throttle: 16,
      minDistance: 5
    })

    this.employeeSignaturePad.addEventListener("endStroke", () => {
      this.updateEmployeeSignatureInput()
    })
  }

  setupCanvasSize(canvas) {
    const container = canvas.parentElement
    const rect = container.getBoundingClientRect()
    
    const dpr = window.devicePixelRatio || 1
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    
    const ctx = canvas.getContext("2d")
    ctx.scale(dpr, dpr)
    
    canvas.style.width = rect.width + "px"
    canvas.style.height = rect.height + "px"
  }

  handleResize() {
    if (this.clientSignaturePad) {
      this.setupCanvasSize(this.clientCanvasTarget)
      this.clientSignaturePad.clear()
    }
    if (this.employeeSignaturePad) {
      this.setupCanvasSize(this.employeeCanvasTarget)
      this.employeeSignaturePad.clear()
    }
  }

  clearClient() {
    this.clientSignaturePad.clear()
    this.clientSignatureInputTarget.value = ""
    this.clientSignedValue = false
    this.updateVisibility()
  }

  clearEmployee() {
    if (this.employeeSignaturePad) {
      this.employeeSignaturePad.clear()
      this.employeeSignatureInputTarget.value = ""
    }
  }

  confirmClientSignature() {
    if (this.clientSignaturePad.isEmpty()) {
      alert("Veuillez signer avant de confirmer.")
      return
    }

    this.updateClientSignatureInput()
    this.clientSignedValue = true
    this.updateVisibility()

    // Scroll to employee section
    this.employeeSectionTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  updateClientSignatureInput() {
    if (!this.clientSignaturePad.isEmpty()) {
      const dataURL = this.clientSignaturePad.toDataURL()
      this.clientSignatureInputTarget.value = dataURL
    } else {
      this.clientSignatureInputTarget.value = ""
    }
  }

  updateEmployeeSignatureInput() {
    if (this.employeeSignaturePad && !this.employeeSignaturePad.isEmpty()) {
      const dataURL = this.employeeSignaturePad.toDataURL()
      this.employeeSignatureInputTarget.value = dataURL
    } else {
      this.employeeSignatureInputTarget.value = ""
    }
  }

  updateVisibility() {
    if (this.clientSignedValue) {
      // Client has signed, show employee section
      this.employeeSectionTarget.classList.remove("hidden")
      this.submitButtonTarget.classList.remove("hidden")

      // Disable client canvas
      this.clientCanvasTarget.classList.add("pointer-events-none", "opacity-50")

      // Initialize employee signature pad after section is visible
      // Use setTimeout to ensure DOM has fully updated
      setTimeout(() => {
        this.initEmployeeSignaturePad()
      }, 100)
    } else {
      // Client hasn't signed yet, hide employee section
      this.employeeSectionTarget.classList.add("hidden")
      this.submitButtonTarget.classList.add("hidden")

      // Enable client canvas
      this.clientCanvasTarget.classList.remove("pointer-events-none", "opacity-50")
    }
  }
}
